import React, { useState, useEffect } from 'react'
import { X, Edit3, Shield, CreditCard } from 'lucide-react'
import { agencyService } from '../services/agencyService'
import { Agent } from '../services/agentService'

interface EditAgentModalProps {
  isOpen: boolean
  agent: Agent | null
  agencyId: string
  onClose: () => void
  onAgentUpdated: () => void
}

interface EditAgentData {
  role: 'agent' | 'admin'
  paymentResponsibility: 'agent' | 'agency' | 'none'
}

export const EditAgentModal: React.FC<EditAgentModalProps> = ({
  isOpen,
  agent,
  agencyId,
  onClose,
  onAgentUpdated
}) => {
  const [formData, setFormData] = useState<EditAgentData>({
    role: 'agent',
    paymentResponsibility: 'agency'
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize form data when agent changes
  useEffect(() => {
    if (agent) {
      setFormData({
        role: agent.role || 'agent',
        paymentResponsibility: agent.paymentResponsibility || 'agency'
      })
    }
  }, [agent])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!agent) {
      setError('No agent selected')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // TODO: Replace with actual API call to update agent
      // await agencyService.updateAgent(agencyId, agent.id, formData)

      // For now, simulate success
      await new Promise(resolve => setTimeout(resolve, 1000))

      onAgentUpdated()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to update agent')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof EditAgentData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  if (!isOpen || !agent) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-md w-full mx-4 max-h-screen overflow-y-auto border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Edit3 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Edit Agent</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {agent.user?.firstName && agent.user?.lastName
                  ? `${agent.user.firstName} ${agent.user.lastName}`
                  : agent.user?.email || 'Unknown User'
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg">
              {error}
            </div>
          )}

          {/* Role Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <Shield className="w-4 h-4 inline mr-2" />
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Payment Responsibility Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <CreditCard className="w-4 h-4 inline mr-2" />
              Payment Responsibility
            </label>
            <select
              value={formData.paymentResponsibility}
              onChange={(e) => handleInputChange('paymentResponsibility', e.target.value)}
              disabled={agent.license?.status === 'none' || agent.license?.status === 'licensed'}
              className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-500 dark:disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <option value="agency">Agency Pays</option>
              <option value="agent">Agent Pays</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4" />
                  Update Agent
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditAgentModal 